import { Check } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { components, GroupBase, OptionsOrGroups, StylesConfig } from 'react-select';
import { DefaultTheme } from 'styled-components';

import { bopsTheme } from '@Themes';
import { StyledSelect } from './select.styles';

// Custom components for compact multiselect mode
const CompactMultiValue = () => null;

const CompactValueContainer = ({ children, ...props }: any) => {
  const { getValue, selectProps } = props;
  const count = getValue().length;
  const { placeholder, compactLabel } = selectProps;

  return (
    <components.ValueContainer {...props}>
      {count > 0 ? (
        <>
          <span className="text-gray-900">
            {compactLabel || placeholder}: {count}
          </span>
          {children[1]}
        </>
      ) : (
        children
      )}
    </components.ValueContainer>
  );
};

const OptionWithCheck = ({ label, isSelected, ...rest }: any) => (
  <components.Option {...rest} label={label} isSelected={isSelected}>
    <div className="flex items-center justify-between w-full">
      <span>{label}</span>
      {isSelected && <Check className="size-4 text-primary" />}
    </div>
  </components.Option>
);

export interface SelectOptions {
  value: string;
  label: string;
}

const Select = ({
  options,
  onChange,
  styles,
  applyTheme,
  defaultValue,
  identifier,
  isClearable,
  placeholder,
  error,
  isMulti,
  value,
  compactMulti,
  compactLabel,
  ariaLabel,
  menuZIndex = 1009,
}: {
  options: OptionsOrGroups<unknown, GroupBase<unknown>>;
  onChange?: ((newValue: any, identifier: string | undefined) => void) | undefined;
  styles?: StylesConfig<unknown, boolean, GroupBase<unknown>>;
  applyTheme?: (theme: DefaultTheme) => DefaultTheme;
  defaultValue?: any;
  identifier?: string;
  placeholder?: string;
  isClearable?: boolean;
  error?: boolean;
  isMulti?: boolean;
  value?: any;
  compactMulti?: boolean;
  compactLabel?: string;
  ariaLabel?: string;
  /** The menu is portaled to body, so a select inside a higher modal layer must lift it above that layer. */
  menuZIndex?: number;
}) => {
  const { t } = useTranslation();

  const customStyles = styles ?? {
    control: (base, state) => ({
      ...base,
      background: bopsTheme.colors.selectBackground,
      color: '#667085',
      // match with the menu
      borderRadius: 4,
      cursor: 'pointer',
      // Overwrittes the different states of border
      borderColor: error ? 'red' : state.isFocused ? bopsTheme.colors.bopsPurple : bopsTheme.colors.cardBorder,
      // Removes weird border around container
    }),
    menu: (base) => ({
      ...base,
      // override border radius to match the box
      color: bopsTheme.colors.fontBlack,
      background: bopsTheme.colors.white,
      borderColor: bopsTheme.colors.cardBorder2,
      borderRadius: '5px',
      position: 'absolute',
      // kill the gap
      marginTop: 5,
    }),
    menuPortal: (base) => ({
      ...base,
      zIndex: menuZIndex,
    }),
    menuList: (base) => ({
      ...base,
      borderColor: bopsTheme.colors.cardBorder,
      // kill the white space on first and last option
      padding: 0,
    }),
  };

  const selectTheme =
    applyTheme ??
    ((theme: DefaultTheme): DefaultTheme => ({
      ...theme,
      colors: {
        ...theme.colors,
        primary: bopsTheme.colors.cardBackgroundTransparent,
        primary25: bopsTheme.colors.cardBackgroundTransparent,
        primary50: bopsTheme.colors.cardBackgroundTransparent,
        neutral80: bopsTheme.colors.black,
      },
    }));

  const internalOnChange = (newValue: any): void => {
    if (onChange) onChange(newValue, identifier);
  };

  const compactComponents = compactMulti
    ? {
        MultiValue: CompactMultiValue,
        ValueContainer: CompactValueContainer,
        Option: OptionWithCheck,
      }
    : undefined;

  const SelectComponent = StyledSelect as any;

  return (
    <SelectComponent
      menuPortalTarget={document.body}
      styles={customStyles}
      theme={selectTheme(bopsTheme)}
      aria-label={ariaLabel}
      placeholder={placeholder ?? t('Global.Select')}
      defaultValue={defaultValue}
      value={value}
      onChange={internalOnChange}
      options={options}
      isClearable={isClearable}
      isMulti={isMulti}
      components={compactComponents}
      compactLabel={compactLabel}
      hideSelectedOptions={compactMulti ? false : undefined}
      closeMenuOnSelect={compactMulti ? false : undefined}
    />
  );
};

export default Select;
